import apiClient from './api';

export interface VideoTranslationJob {
  id: string;
  title?: string;
  status: 'pending' | 'processing' | 'awaiting_review' | 'completed' | 'failed' | 'cancelled';
  progress: number;
  currentStep: string;
  steps: any[];
  request: any;
  result?: any;
  error?: string;
  youtubeUploads: YouTubeUploadEntry[];
  thumbnailUrl?: string;
  createdAt: string;
  updatedAt: string;
}

export interface VideoUploadResponse {
  success: boolean;
  fileId: string;
  originalName: string;
  size: number;
  mimeType: string;
}

export interface VideoTranslationResponse {
  success: boolean;
  jobId: string;
  message?: string;
}

export interface VideoJobsResponse {
  success: boolean;
  jobs: VideoTranslationJob[];
  pagination?: {
    total: number;
    page: number;
    limit: number;
  };
}

export interface VideoJobResponse {
  success: boolean;
  job: VideoTranslationJob;
}

export interface VideoJobCancelResponse {
  success: boolean;
  message?: string;
}

export interface VideoStepUpdateResponse {
  success: boolean;
  message?: string;
}

export interface VideoStepExecuteResponse {
  success: boolean;
  message?: string;
}

export interface VideoHealthResponse {
  success: boolean;
  message?: string;
}

export interface YouTubeUploadEntry {
  videoId: string;
  title: string;
  description?: string;
  privacyStatus: 'private' | 'unlisted' | 'public';
  publishedAt: string;
  videoUrl: string;
  videoTranslationJobId?: string | null;
  videoUploadId?: string | null;
}

export interface RawVideoUpload {
  fileId: string;
  originalName: string;
  description?: string;
  tags?: string[];
  size: number;
  uploadedAt: string;
  youtubeUploads: YouTubeUploadEntry[];
  thumbnailUrl?: string;
}

export interface RawVideoUploadsResponse {
  success: boolean;
  uploads: RawVideoUpload[];
}

export interface VideoDetailsSummary {
  type: 'upload' | 'translation';
  id: string;
  title: string;
  status: string;
  sourceLanguage?: string;
  targetLanguage?: string;
  size?: number;
  mimeType?: string;
  createdAt: string;
}

export interface VideoDetailsChannel {
  platform: 'youtube' | 'facebook' | 'instagram' | 'whatsapp' | 'tiktok' | 'x';
  channelName: string;
  status: 'pending' | 'scheduled' | 'processing' | 'completed' | 'failed';
  publishedAt?: string;
  url?: string;
  metrics: { views: number; likes: number; comments: number } | null;
}

export interface VideoDetailsResponse {
  success: boolean;
  video: VideoDetailsSummary;
  channels: VideoDetailsChannel[];
}

export const videoApi = {
  // Step 1: Upload video file, returns fileId. Supports upload progress tracking.
  // `details` is optional metadata (title/description/tags) captured alongside a plain
  // library upload — unrelated to the translation pipeline's own fileId-only usage.
  uploadVideo: async (
    file: File,
    onUploadProgress?: (percentCompleted: number) => void,
    details?: { title?: string; description?: string; tags?: string }
  ): Promise<VideoUploadResponse> => {
    const formData = new FormData();
    formData.append('video', file);
    if (details?.title) formData.append('title', details.title);
    if (details?.description) formData.append('description', details.description);
    if (details?.tags) formData.append('tags', details.tags);
    const response = await apiClient.post('/video/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0,
      onUploadProgress: (progressEvent) => {
        if (onUploadProgress && progressEvent.total) {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onUploadProgress(percent);
        }
      },
    });
    return response.data;
  },

  // Upload an audio file directly, returns fileId. Used for Avatar-mode translations
  // sourced from audio instead of a video file — mirrors uploadVideo().
  uploadAudioFile: async (
    file: File,
    onUploadProgress?: (percentCompleted: number) => void,
    details?: { title?: string }
  ): Promise<VideoUploadResponse> => {
    const formData = new FormData();
    formData.append('audio', file);
    if (details?.title) formData.append('title', details.title);
    const response = await apiClient.post('/video/upload-audio', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0,
      onUploadProgress: (progressEvent) => {
        if (onUploadProgress && progressEvent.total) {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onUploadProgress(percent);
        }
      },
    });
    return response.data;
  },

  // Fetch a video from a direct file URL and store it exactly like a regular upload, returning
  // a fileId. The URL must point directly at a video file — YouTube and other streaming/social
  // platforms are rejected server-side (they have download restrictions).
  uploadVideoFromUrl: async (url: string): Promise<VideoUploadResponse> => {
    const response = await apiClient.post('/video/upload-from-url', { url });
    return response.data;
  },

  // Fetch an audio file from a direct URL and store it, returning a fileId. Used for
  // Avatar-mode translations sourced from an audio link instead of a video — the same
  // host denylist and content-type validation as uploadVideoFromUrl applies, just for
  // audio/* content types.
  uploadAudioFromUrl: async (url: string): Promise<VideoUploadResponse> => {
    const response = await apiClient.post('/video/upload-audio-from-url', { url });
    return response.data;
  },

  // Step 2: Start translation job using a previously uploaded (file, video-URL, or
  // audio-URL) fileId. Returns jobId immediately.
  startTranslation: async (params: {
    fileId?: string;
    title?: string;
    targetLanguage: string;
    voiceId: string;
    sourceLanguage?: string;
    sttProvider?: 'sarvam' | 'ai4bharat';
    translationProvider?: 'sarvam' | 'google' | 'deepl';
    narrationMode?: 'formal' | 'modern-colloquial' | 'classic-colloquial' | 'code-mixed';
    ttsProvider?: 'elevenlabs' | 'sarvam' | 'ai4bharat';
    ttsSpeed?: number;
    outputFormat?: string;
    videoQuality?: string;
    lipsyncModel?: string;
    enhanceAudio?: boolean;
    preserveEmotion?: boolean;
    customPrompt?: string;
    videoMode?: 'dubbed' | 'avatar' | 'lipsynced';
    avatarId?: string;
    refPoseFileId?: string;
    requireTranslationReview?: boolean;
  }): Promise<VideoTranslationResponse> => {
    const response = await apiClient.post('/video/translate', params);
    return response.data;
  },

  // Upload a reference pose video for SadTalker head movement. Returns a refPoseFileId.
  uploadRefPoseVideo: async (
    file: File,
    onUploadProgress?: (percentCompleted: number) => void
  ): Promise<{ success: boolean; refPoseFileId: string; originalName: string; size: number }> => {
    const formData = new FormData();
    formData.append('refPoseVideo', file);
    const response = await apiClient.post('/video/upload-ref-pose', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0,
      onUploadProgress: (progressEvent) => {
        if (onUploadProgress && progressEvent.total) {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onUploadProgress(percent);
        }
      },
    });
    return response.data;
  },

  // Get job status
  getJob: async (jobId: string) => {
    const response = await apiClient.get(`/video/jobs/${jobId}`);
    return response.data;
  },

  // Get all jobs
  getJobs: async (params?: { status?: string; limit?: number; offset?: number }) => {
    const response = await apiClient.get('/video/jobs', { params });
    return response.data;
  },

  // Get raw uploaded video files
  getUploads: async (): Promise<RawVideoUploadsResponse> => {
    const response = await apiClient.get('/video/uploads');
    return response.data;
  },

  // Rename raw uploaded video
  renameUpload: async (fileId: string, originalName: string): Promise<{ success: boolean; upload: RawVideoUpload }> => {
    const response = await apiClient.put(`/video/uploads/${fileId}`, { originalName });
    return response.data;
  },

  // Download a raw uploaded video via signed S3 URL
  downloadUpload: async (fileId: string): Promise<void> => {
    const response = await apiClient.get<{ success: boolean; url: string }>(`/video/uploads/${fileId}/download`);
    if (response.data?.url) {
      window.open(response.data.url, '_blank');
    }
  },

  // Get a signed shareable URL for a raw uploaded video
  getUploadShareableUrl: async (fileId: string): Promise<{ success: boolean; url: string }> => {
    const response = await apiClient.get<{ success: boolean; url: string }>(`/video/uploads/${fileId}/url`);
    return response.data;
  },

  // Get a signed shareable URL for a translated video
  getJobShareableUrl: async (jobId: string, download = false): Promise<{ success: boolean; url: string }> => {
    const response = await apiClient.get<{ success: boolean; url: string }>(`/video/jobs/${jobId}/url`, {
      params: download ? { download: 'true' } : undefined,
    });
    return response.data;
  },

  // Get a video's details plus everywhere it's been published (and, for YouTube, live metrics)
  getVideoDetails: async (type: 'upload' | 'translation', id: string): Promise<VideoDetailsResponse> => {
    const response = await apiClient.get<VideoDetailsResponse>(`/video/details/${type}/${id}`);
    return response.data;
  },

  // Download the final translated video. Fetches a presigned S3 URL (with an
  // attachment Content-Disposition) via an authenticated JSON call, then
  // triggers the download via a plain browser navigation. We deliberately
  // avoid blob-fetching the /video/files/:jobId/:filename redirect here: that
  // route issues a 302 to S3, and following it via XHR/fetch to read the body
  // as a blob requires the S3 bucket to have CORS configured for this origin
  // (it usually doesn't), which silently breaks the download.
  downloadFinalVideo: async (jobId: string, apiUrl: string, suggestedFilename: string): Promise<void> => {
    try {
      const { url } = await videoApi.getJobShareableUrl(jobId, true);
      const a = document.createElement('a');
      a.href = url;
      a.download = suggestedFilename;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch {
      // S3 not configured on this deployment (or no signed URL available) —
      // fall back to the legacy blob-fetch, which works when /video/files
      // streams the file directly instead of redirecting to S3.
      await videoApi.downloadFile(apiUrl, suggestedFilename);
    }
  },

  // Cancel job
  cancelJob: async (jobId: string) => {
    const response = await apiClient.post(`/video/jobs/${jobId}/cancel`);
    return response.data;
  },

  // Job-level retry — auto-detects the first non-completed step
  retryJob: async (jobId: string) => {
    const response = await apiClient.post(`/video/jobs/${jobId}/retry`);
    return response.data;
  },

  // Update job title
  updateJobTitle: async (jobId: string, title: string): Promise<{ success: boolean; title: string }> => {
    const response = await apiClient.put(`/video/jobs/${jobId}/title`, { title });
    return response.data;
  },

  // Update step data
  updateStepData: async (jobId: string, stepId: string, data: any) => {
    const response = await apiClient.put(`/video/jobs/${jobId}/steps/${stepId}`, data);
    return response.data;
  },

  // Execute step (recomputes the step itself). Pass overrides to change the
  // engine/provider used. By default this cascades — the step and everything
  // after it reruns to completion. Pass runAlone: true to stop right after
  // this one step finishes, leaving downstream steps' cached output untouched.
  executeStep: async (
    jobId: string,
    stepId: string,
    overrides?: {
      sttProvider?: 'sarvam' | 'ai4bharat';
      translationProvider?: 'sarvam' | 'google' | 'deepl';
      narrationMode?: 'formal' | 'modern-colloquial' | 'classic-colloquial' | 'code-mixed';
      ttsProvider?: 'elevenlabs' | 'sarvam' | 'ai4bharat';
      voiceId?: string;
      lipsyncModel?: 'wav2lip' | 'video-retalking';
    },
    runAlone?: boolean
  ) => {
    const response = await apiClient.post(`/video/jobs/${jobId}/steps/${stepId}/execute`, { ...(overrides ?? {}), runAlone: !!runAlone });
    return response.data;
  },

  // Continue past a completed step WITHOUT recomputing it — used after editing a
  // step's text (transcription/translation) so the edit propagates forward instead
  // of being overwritten by a fresh recompute, and to approve a translation-review pause.
  continueStep: async (
    jobId: string,
    stepId: string,
    edits?: { transcribedText?: string; translatedText?: string }
  ) => {
    const response = await apiClient.post(`/video/jobs/${jobId}/steps/${stepId}/continue`, edits ?? {});
    return response.data;
  },

  // Fetch an intermediate audio artifact (extracted/translated audio) as a playable/downloadable blob URL.
  getAudioBlobUrl: async (jobId: string, filename: string): Promise<string> => {
    const response = await apiClient.get(`/video/audio/${jobId}/${filename}`, { responseType: 'blob' });
    return URL.createObjectURL(response.data);
  },

  // Download a protected video file using auth headers
  downloadFile: async (apiUrl: string, suggestedFilename: string): Promise<void> => {
    const path = apiUrl.replace(/^https?:\/\/[^/]+/, '').replace(/^\/api/, '');
    const response = await apiClient.get(path, { responseType: 'blob' });
    const blobUrl = URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = suggestedFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  },

  // Get service health
  getHealth: async () => {
    const response = await apiClient.get('/video/health');
    return response.data;
  },
};